import {
  changeActualsType,
  selectActualsType,
  selectAvailableActualsTypes,
  selectProgramType,
  selectProgramTypesExtended,
} from 'legacy/lut/features/partner360/configuration'
import { selectActualsData } from 'legacy/lut/features/partner360/configuration/selectors/selectActualsData'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { ActualsValueTypes } from '../../../models/actualValueTypes'

export const useActualsTypes = () => {
  /** -------------------- Variables ----------------------- */
  const dispatch = useAppDispatch()
  const actualsType = useSelector(selectActualsType)
  const actualsTypes = useSelector(selectAvailableActualsTypes)
  const programType = useSelector(selectProgramType)
  const programTypesExtended = useSelector(selectProgramTypesExtended)
  const { isPartnerUser } = useUserRoles()
  const { valueTypeDefault: actualsTypeDefault } = useSelector(selectActualsData)
  const partnerActualsTypeDefault = isPartnerUser ? actualsTypeDefault : null

  const offeringActualsTypes = useMemo(() => {
    const offering = programTypesExtended.find((it) => it.Offering === programType)
    if (!offering) {
      return null
    }

    if (isPartnerUser) {
      return partnerActualsTypeDefault ? [partnerActualsTypeDefault] : null
    }

    if (!offering.ActualsValueTypes) {
      return null
    }

    const parsed = offering.ActualsValueTypes.split(',').filter(Boolean) as ActualsValueTypes[]

    return parsed.length ? parsed : null
  }, [isPartnerUser, partnerActualsTypeDefault, programTypesExtended, programType])

  const baseActualsTypes = useMemo(() => {
    return offeringActualsTypes ?? actualsTypes
  }, [offeringActualsTypes, actualsTypes])

  const { pdfActualsTypes, pdfActualsTypeDefault } = useMemo(() => {
    const currentOffering = programTypesExtended.find((it) => it.Offering === programType)
    const parsed = (currentOffering?.PDFActualsValueTypes || '')
      .split(',')
      .filter(Boolean) as ActualsValueTypes[]

    const pdfActualsTypeDefault = currentOffering?.PDFActualsValueTypeDefault as ActualsValueTypes
    // Preserve the PDF fallback independently of the screen's partner default.
    let fallbackTypes = baseActualsTypes
    if (isPartnerUser) {
      fallbackTypes = pdfActualsTypeDefault ? [pdfActualsTypeDefault] : actualsTypes
    }

    return { pdfActualsTypes: parsed.length ? parsed : fallbackTypes, pdfActualsTypeDefault }
  }, [programTypesExtended, baseActualsTypes, programType, isPartnerUser, actualsTypes])

  /** -------------------- Methods ----------------------- */
  const handleOptionClick = useCallback(
    (newValue: ActualsValueTypes) => {
      dispatch(changeActualsType(newValue))
    },
    [dispatch]
  )

  return {
    actualsType,
    actualsTypeDefault,
    actualsTypes: baseActualsTypes,
    pdfActualsTypes,
    pdfActualsTypeDefault,
    onChangeActualsType: handleOptionClick,
  }
}
